import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, List, Tag, Button, Space } from 'antd';
import { 
  FileTextOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  BarChartOutlined,
  ReloadOutlined,
  UpOutlined,
  DownOutlined
} from '@ant-design/icons';
import apiClient from '../services/api';
import { AuditLogStatistics } from '../types';

const AuditLogStatisticsComponent: React.FC = () => {
  const [statistics, setStatistics] = useState<AuditLogStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [userMap, setUserMap] = useState<{[id: number]: string}>({});
  const [employeeMap, setEmployeeMap] = useState<{[id: number]: string}>({});
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    fetchStatistics();
    fetchUsers();
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await apiClient.get('/employees?all=1');
      const employees = Array.isArray(response) ? response : (response as any).data || response;
      const map: {[id: number]: string} = {};
      employees.forEach((e: any) => { 
        map[e.id] = e.full_name || e.employee_code || `Employee ${e.id}`; 
      });
      setEmployeeMap(map);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users?all=1');
      const users = Array.isArray(response) ? response : (response as any).data || response;
      const map: {[id: number]: string} = {};
      users.forEach((u: any) => { 
        map[u.id] = u.full_name || u.username || u.email || `User ${u.id}`; 
      });
      setUserMap(map);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const translateActionType = (actionType: string) => {
    const actionMap: { [key: string]: string } = {
      // Asset actions
      'ASSET_CREATE': 'Tạo tài sản',
      'ASSET_UPDATE': 'Cập nhật tài sản',
      'ASSET_DELETE': 'Xóa tài sản',
      'CREATE_ASSET': 'Tạo tài sản',
      'UPDATE_ASSET': 'Cập nhật tài sản',
      'DELETE_ASSET': 'Xóa tài sản',
      
      // Employee actions
      'EMPLOYEE_CREATE': 'Tạo nhân viên',
      'EMPLOYEE_UPDATE': 'Cập nhật nhân viên',
      'EMPLOYEE_DELETE': 'Xóa nhân viên',
      'CREATE_EMPLOYEE': 'Tạo nhân viên',
      'UPDATE_EMPLOYEE': 'Cập nhật nhân viên',
      'DELETE_EMPLOYEE': 'Xóa nhân viên',
      
      // User actions
      'USER_CREATE': 'Tạo người dùng',
      'USER_UPDATE': 'Cập nhật người dùng',
      'USER_DELETE': 'Xóa người dùng',
      'CREATE_USER': 'Tạo người dùng',
      'UPDATE_USER': 'Cập nhật người dùng',
      'DELETE_USER': 'Xóa người dùng',
      'PASSWORD_CHANGE': 'Đổi mật khẩu',
      
      // Company actions
      'COMPANY_CREATE': 'Tạo công ty',
      'COMPANY_UPDATE': 'Cập nhật công ty',
      'COMPANY_DELETE': 'Xóa công ty',
      'CREATE_COMPANY': 'Tạo công ty',
      'UPDATE_COMPANY': 'Cập nhật công ty',
      'DELETE_COMPANY': 'Xóa công ty',
      
      // Location actions
      'LOCATION_CREATE': 'Tạo vị trí',
      'LOCATION_UPDATE': 'Cập nhật vị trí',
      'LOCATION_DELETE': 'Xóa vị trí',
      'CREATE_LOCATION': 'Tạo vị trí',
      'UPDATE_LOCATION': 'Cập nhật vị trí',
      'DELETE_LOCATION': 'Xóa vị trí',
      
      // Supplier actions
      'SUPPLIER_CREATE': 'Tạo nhà cung cấp',
      'SUPPLIER_UPDATE': 'Cập nhật nhà cung cấp',
      'SUPPLIER_DELETE': 'Xóa nhà cung cấp',
      'CREATE_SUPPLIER': 'Tạo nhà cung cấp',
      'UPDATE_SUPPLIER': 'Cập nhật nhà cung cấp',
      'DELETE_SUPPLIER': 'Xóa nhà cung cấp',
      
      // Category actions
      'ASSET_CATEGORY_CREATE': 'Tạo danh mục tài sản',
      'ASSET_CATEGORY_UPDATE': 'Cập nhật danh mục tài sản',
      'ASSET_CATEGORY_DELETE': 'Xóa danh mục tài sản',
      'CREATE_ASSET_CATEGORY': 'Tạo danh mục tài sản',
      'UPDATE_ASSET_CATEGORY': 'Cập nhật danh mục tài sản',
      'DELETE_ASSET_CATEGORY': 'Xóa danh mục tài sản',
      
      // Auth actions
      'LOGIN': 'Đăng nhập',
      'LOGOUT': 'Đăng xuất',
      'LOGIN_FAILED': 'Đăng nhập thất bại',
      'TOKEN_REFRESH': 'Làm mới token',
      
      // Maintenance actions
      'MAINTENANCE_CREATE': 'Tạo bảo trì',
      'MAINTENANCE_UPDATE': 'Cập nhật bảo trì',
      'MAINTENANCE_DELETE': 'Xóa bảo trì',
      'CREATE_MAINTENANCE': 'Tạo bảo trì',
      'UPDATE_MAINTENANCE': 'Cập nhật bảo trì',
      'DELETE_MAINTENANCE': 'Xóa bảo trì',
      
      // Inventory audit actions
      'INVENTORY_AUDIT_CREATE': 'Tạo kiểm kê',
      'INVENTORY_AUDIT_UPDATE': 'Cập nhật kiểm kê',
      'INVENTORY_AUDIT_DELETE': 'Xóa kiểm kê',
      'CREATE_INVENTORY_AUDIT': 'Tạo kiểm kê',
      'UPDATE_INVENTORY_AUDIT': 'Cập nhật kiểm kê',
      'DELETE_INVENTORY_AUDIT': 'Xóa kiểm kê',
      
      // Disposal actions
      'DISPOSAL_REQUEST_CREATE': 'Tạo yêu cầu thanh lý',
      'DISPOSAL_REQUEST_UPDATE': 'Cập nhật yêu cầu thanh lý',
      'DISPOSAL_REQUEST_DELETE': 'Xóa yêu cầu thanh lý',
      'CREATE_DISPOSAL_REQUEST': 'Tạo yêu cầu thanh lý',
      'UPDATE_DISPOSAL_REQUEST': 'Cập nhật yêu cầu thanh lý',
      'DELETE_DISPOSAL_REQUEST': 'Xóa yêu cầu thanh lý',
      
      // Transfer actions
      'TRANSFER_REQUEST_CREATE': 'Tạo yêu cầu điều chuyển',
      'TRANSFER_REQUEST_UPDATE': 'Cập nhật yêu cầu điều chuyển',
      'TRANSFER_REQUEST_DELETE': 'Xóa yêu cầu điều chuyển',
      'CREATE_TRANSFER_REQUEST': 'Tạo yêu cầu điều chuyển',
      'UPDATE_TRANSFER_REQUEST': 'Cập nhật yêu cầu điều chuyển',
      'DELETE_TRANSFER_REQUEST': 'Xóa yêu cầu điều chuyển',
    };
    
    return actionMap[actionType] || actionType;
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getSystemAuditStatistics();
      setStatistics(response as AuditLogStatistics);
    } catch (error) {
      console.error('Error fetching audit statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card title="Thống kê nhật ký hệ thống" loading={true} style={{ marginBottom: 16 }} />
    );
  }

  if (!statistics) {
    return (
      <Card title="Thống kê nhật ký hệ thống" style={{ marginBottom: 16 }}>
        <p>Không thể tải thống kê</p>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <BarChartOutlined style={{ marginRight: 8 }} />
            Thống kê nhật ký hệ thống
          </span>
          <Space>
            <Button
              type="text"
              icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="small"
              title={isCollapsed ? "Mở rộng" : "Thu gọn"}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchStatistics}
              size="small"
              title="Làm mới"
            />
          </Space>
        </div>
      } 
      style={{ marginBottom: 16 }}
          >
        {!isCollapsed && (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Tổng số logs"
                  value={statistics.total_logs}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Logs hôm nay"
                  value={statistics.logs_today}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Logs tuần này"
                  value={statistics.logs_this_week}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Logs tháng này"
                  value={statistics.logs_this_month}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 24 }}>
              <Col xs={24} md={12}>
                <Card title="Top hành động" size="small">
                  <List
                    size="small"
                    dataSource={statistics.top_actions}
                    renderItem={(item, index) => (
                      <List.Item>
                        <List.Item.Meta
                          title={
                            <span>
                              <Tag color={index < 3 ? '#1890ff' : '#d9d9d9'}>
                                #{index + 1}
                              </Tag>
                              {translateActionType(item.action_type)}
                            </span>
                          }
                          description={`${item.count} lần thực hiện`}
                        />
                      </List.Item>
                    )}
                  />
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title="Top người dùng" size="small">
                  <List
                    size="small"
                    dataSource={statistics.top_users}
                    renderItem={(item, index) => (
                      <List.Item>
                        <List.Item.Meta
                          title={
                            <span>
                              <Tag color={index < 3 ? '#52c41a' : '#d9d9d9'}>
                                #{index + 1}
                              </Tag>
                                                        <UserOutlined style={{ marginRight: 4 }} />
                          {userMap[item.user_id] || employeeMap[item.user_id] || `User ${item.user_id}`}
                            </span>
                          }
                          description={`${item.count} hành động`}
                        />
                      </List.Item>
                    )}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card>
  );
};

export default AuditLogStatisticsComponent; 